import { Component, ElementRef, Inject, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import jsQR from 'jsqr';

export interface QrScanResult {
  membershipNumber: string;
  timeToScanMs: number;
}

export type QrScanTracker = (eventName: string, properties?: Record<string, any>) => void;

@Component({
  selector: 'qr-scan-dialog',
  templateUrl: './qr-scan-dialog.component.html',
  styleUrls: ['./qr-scan-dialog.component.scss'],
  imports: [CommonModule, MatButtonModule],
  standalone: true
})
export class QrScanDialogComponent implements OnInit, OnDestroy {
  // Frames right after the camera starts can be stale, so they are not scanned
  private static readonly warmUpMs = 750;

  @ViewChild('video', { static: true }) public videoElement: ElementRef<HTMLVideoElement>;

  public errorText = '';
  public isCameraReady = false;

  private readonly canvas = document.createElement('canvas');
  private cameraReadyTime = 0;
  private destroyed = false;
  private lastUnrecognizedCode = '';
  private scanStartTime = 0;
  private frameRequest: number;
  private stream: MediaStream;

  constructor(
    public dialogRef: MatDialogRef<QrScanDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private readonly dialogData: { track?: QrScanTracker }) {}

  public ngOnInit() {
    this.startCamera();
  }

  private async startCamera() {
    if (!navigator.mediaDevices?.getUserMedia) {
      this.errorText = 'Camera access is not available in this browser.';
      this.track('QR Scan Failed', { Reason: 'No Camera Support' });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      if (this.destroyed) {
        // Dialog was closed while waiting on camera permission
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      this.stream = stream;
      const video = this.videoElement.nativeElement;
      video.srcObject = this.stream;
      await video.play();
      if (this.destroyed) {
        return;
      }
      this.showVideoOnFirstFrame(video);
      this.scanFrame();
    } catch (error) {
      if (this.destroyed) {
        // Closing the dialog while the camera starts rejects play(); that is a cancel, not a camera failure
        return;
      }
      this.errorText = 'Unable to access the camera. Check your browser camera permissions and try again.';
      const permissionDenied = error?.name === 'NotAllowedError' || error?.name === 'SecurityError';
      this.track('QR Scan Failed', { Reason: permissionDenied ? 'Permission Denied' : 'Camera Error' });
    }
  }

  public ngOnDestroy() {
    this.destroyed = true;
    cancelAnimationFrame(this.frameRequest);
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
    }
    this.videoElement.nativeElement.srcObject = null;
  }

  // The static loyalty QR code is "043", padding spaces, the 14-digit membership number, a "-" and then the mobile pay
  // payment info. Only the membership number is used; the payment info is never kept. A bare 14-digit membership number
  // (spaces allowed) is also accepted. Anything else, such as the new 17-digit expiring scan codes or an unrelated
  // QR code, returns '' so scanning continues.
  public static extractMembershipNumber(qrData: string): string {
    const data = (qrData || '').trim();
    const bareNumber = data.replaceAll(/\s/g, '');
    if (/^\d{14}$/.test(bareNumber)) {
      return QrScanDialogComponent.isMembershipNumber(bareNumber) ? bareNumber : '';
    }
    const match = /^043\s*(\d{14})/.exec(data);
    return match && QrScanDialogComponent.isMembershipNumber(match[1]) ? match[1] : '';
  }

  // Loyalty card numbers are in the 89 and 88 bin ranges
  private static isMembershipNumber(cardNumber: string): boolean {
    return /^8[89]\d{12}$/.test(cardNumber);
  }

  // Only the length is tracked, never the contents of the code
  private trackUnrecognizedCode(data: string) {
    if (data && data !== this.lastUnrecognizedCode) {
      this.lastUnrecognizedCode = data;
      this.track('QR Scan Unrecognized Code', { 'Code Length': data.length });
    }
  }

  private track(eventName: string, properties?: Record<string, any>) {
    this.dialogData?.track?.(eventName, properties);
  }

  // Some cameras briefly show their last captured frame, so keep the preview hidden until a fresh frame arrives
  private showVideoOnFirstFrame(video: HTMLVideoElement) {
    if ('requestVideoFrameCallback' in video) {
      (video as any).requestVideoFrameCallback(() => this.markCameraReady());
    } else {
      this.markCameraReady();
    }
  }

  private markCameraReady() {
    this.isCameraReady = true;
    this.cameraReadyTime = performance.now();
    this.scanStartTime = performance.now() + QrScanDialogComponent.warmUpMs;
  }

  private scanFrame() {
    if (this.destroyed) {
      return;
    }
    const video = this.videoElement.nativeElement;
    if (this.isCameraReady && performance.now() >= this.scanStartTime
        && video.readyState === video.HAVE_ENOUGH_DATA && video.videoWidth) {
      this.canvas.width = video.videoWidth;
      this.canvas.height = video.videoHeight;
      const context = this.canvas.getContext('2d', { willReadFrequently: true });
      context.drawImage(video, 0, 0, this.canvas.width, this.canvas.height);
      const image = context.getImageData(0, 0, this.canvas.width, this.canvas.height);
      const code = jsQR(image.data, image.width, image.height, { inversionAttempts: 'dontInvert' });
      const membershipNumber = code && QrScanDialogComponent.extractMembershipNumber(code.data);
      if (membershipNumber) {
        const result: QrScanResult = { membershipNumber, timeToScanMs: Math.round(performance.now() - this.cameraReadyTime) };
        this.dialogRef.close(result);
        return;
      }
      this.trackUnrecognizedCode(code?.data);
    }
    this.frameRequest = requestAnimationFrame(() => this.scanFrame());
  }
}
